/**
 * Helper functions for order card display data.
 * Normalizes order and item structures from API responses for use in OrderListingCard components.
 */

const WALK_IN_CUSTOMER_LABEL = "Walk-In Customer";

const normalizeWalkInNameKey = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

/**
 * True when the order has no identifiable customer (in-store walk-in).
 * Matches legacy table logic: no billing/customer name and no contact details.
 */
export function isWalkInCustomer(order) {
  if (!order) return true;

  const billingName = String(order.customer_name || order.billing_name || "").trim();
  const phone = String(
    order.delivery_phn ||
      order.billing_phn ||
      order.customer_phone ||
      order.phone ||
      ""
  ).trim();
  const email = String(
    order.email || order.customer_email || order.billing_email || ""
  ).trim();

  if (normalizeWalkInNameKey(billingName) === "walkincustomer") {
    return true;
  }

  return !billingName && !phone && !email;
}

/**
 * Extracts display data from an order object for use in order cards.
 * Handles both in-store and online order structures.
 *
 * @param {Object} order - Raw order object from API
 * @returns {Object} Normalized display data
 */
export function getOrderCardDisplayData(order) {
  if (!order) return {};

  const toPositiveNumber = (value) => {
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  };
  const hasPositiveFlag = (value) => toPositiveNumber(value) > 0;

  const normalizePaymentLabel = ({
    paymentMethodValue,
    paymentIdValue,
    cardTypeValue,
    isSplitPaymentValue,
    isGiftCardValue,
    isStoreCreditValue,
    isLotteryScratchValue,
  }) => {
    const rawPaymentMethod = String(paymentMethodValue || "").trim();
    const rawPaymentId = String(paymentIdValue || "").trim();
    const normalizedPaymentMethod =
      rawPaymentMethod === "-" || rawPaymentMethod === "—" ? "" : rawPaymentMethod;
    const normalizedPaymentId =
      rawPaymentId === "-" || rawPaymentId === "—" ? "" : rawPaymentId;
    const normalizedCardType = String(cardTypeValue || "").trim();
    const normalizedCardTypeLower = normalizedCardType.toLowerCase();

    const splitPaymentByFlag = hasPositiveFlag(isSplitPaymentValue);
    const specialPaymentTypes = [
      { isActive: hasPositiveFlag(isGiftCardValue), label: "Gift Card" },
      { isActive: hasPositiveFlag(isStoreCreditValue), label: "Store Credit" },
      { isActive: hasPositiveFlag(isLotteryScratchValue), label: "Lottery" },
    ].filter((item) => item.isActive);

    if (splitPaymentByFlag || specialPaymentTypes.length > 1) return "Split Payment";
    if (specialPaymentTypes.length === 1) return specialPaymentTypes[0].label;

    if (normalizedCardType.startsWith("FoodEbt/")) return "Food EBT";
    if (normalizedCardType.startsWith("CashEbt/")) return "Cash EBT";
    if (normalizedCardType.startsWith("Credit/")) return "Credit Card";
    if (normalizedCardType.startsWith("Debit/")) return "Debit Card";

    if (normalizedCardTypeLower.includes("food")) return "Food EBT";
    if (normalizedCardTypeLower.includes("cash") && normalizedCardTypeLower.includes("ebt")) {
      return "Cash EBT";
    }
    if (normalizedCardTypeLower.includes("credit")) return "Credit Card";
    if (normalizedCardTypeLower.includes("debit")) return "Debit Card";

    if (
      normalizedPaymentMethod.toLowerCase() === "cash" ||
      normalizedPaymentId.toLowerCase() === "cash" ||
      normalizedCardTypeLower === "cash"
    ) {
      return "Cash";
    }

    if (
      normalizedPaymentMethod === "00000" ||
      normalizedPaymentId === "00000" ||
      /^\d+$/.test(normalizedPaymentMethod) ||
      /^\d+$/.test(normalizedPaymentId)
    ) {
      return "Credit Card";
    }

    if (!normalizedPaymentMethod && !normalizedPaymentId) {
      return "Card";
    }

    return normalizedPaymentMethod || normalizedPaymentId || "";
  };

  const cartData =
    order.items ||
    order.cart_data ||
    order.order_product ||
    order.order_products ||
    [];
  const itemsArray = Array.isArray(cartData) ? cartData : [];

  const isWalkIn = isWalkInCustomer(order);
  const customerName =
    order.customer_name ||
    order.billing_name ||
    (isWalkIn ? WALK_IN_CUSTOMER_LABEL : "N/A");

  const employeeName =
    order.employee_name ||
    (order.f_name || order.l_name
      ? `${order.f_name || ""} ${order.l_name || ""}`.trim()
      : "");

  const amt = order.amt ?? order.total_amount ?? 0;
  const totalAmountFormatted = amt
    ? `$${parseFloat(amt).toFixed(2)}`
    : "$0.00";

  const paymentMethod =
    order.payment_method ??
    order.paymentMethod ??
    (order.payment_id === "00000" ? "Credit Card" : order.payment_id);

  const paymentId = order.payment_id ?? order.payment_method ?? order.paymentMethod;
  const resolvedPaymentType = normalizePaymentLabel({
    paymentMethodValue: order.payment_method ?? order.paymentMethod,
    paymentIdValue: paymentId,
    cardTypeValue: order.card_type,
    isSplitPaymentValue: order.is_split_payment,
    isGiftCardValue: order.is_gift_card,
    isStoreCreditValue: order.is_store_credit,
    isLotteryScratchValue: order.is_lottery_scratch,
  });

  return {
    orderId: order.order_id || "",
    customerName,
    isWalkInCustomer: isWalkIn,
    totalAmountFormatted,
    orderDateRaw: order.merchant_time || order.order_date || order.created_at,
    employeeName,
    pointsEarned: (() => {
      const raw =
        order.loyalty_points_earned ??
        order.loyalty_point_earned ??
        order.loyalty_points ??
        order.points_earned ??
        0;
      if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
      const str = String(raw || "").replace(/^["']|["']$/g, "").trim();
      const num = parseFloat(str);
      return Number.isNaN(num) ? 0 : num;
    })(),
    orderStatus: (order.order_status || "").toLowerCase(),
    itemsArray,
    itemCount: itemsArray.length || order.item_count || 1,
    refundAmount: parseFloat(order.refund_amount) || 0,
    totalAmountNum: parseFloat(order.amt ?? order.total_amount) || 0,
    paymentMethod: paymentMethod || "—",
    paymentDisplayType: resolvedPaymentType || "",
    payment_id: paymentId,
    is_split_payment: order.is_split_payment,
    card_type: order.card_type,
    // Online order specific
    m_status: order.m_status,
    order_method: order.order_method,
  };
}

/**
 * Extracts display data from an order item for use in item rows.
 *
 * @param {Object} item - Raw item object from order
 * @returns {Object} Normalized item display data
 */
export function getOrderItemDisplayData(item) {
  if (!item) return {};

  const name =
    item.product_name || item.name || item.item_name || "Product Item";
  const qty = item.qty ?? item.quantity ?? 1;
  const upc = item.upc || item.sku || "";

  const img =
    item.product_image || item.image || (item.img != null ? item.img : "") || "";
  const productImage =
    img && typeof img === "string" && img.includes(",")
      ? img.split(",")[0]?.trim()
      : img;

  return {
    id: item.id ?? item.product_id,
    name,
    upc,
    qty,
    product_image: productImage || item.product_image || item.image,
    price: item.price || item.unit_price,
    amt: item.amt,
  };
}

/**
 * Returns the image URL for an order item.
 * Handles both full URLs and relative paths.
 * API may return "img" as comma-separated filenames - uses first one.
 *
 * @param {Object} itemData - Item object (from getOrderItemDisplayData or raw)
 * @param {string} baseUrl - Base URL for product images (e.g. Config.PRODUCT_IMAGES_BASE_URL)
 * @param {string} [merchantId] - Store id for upload/products/{merchantId}/{filename}
 * @returns {string} Full image URL
 */
export function getOrderItemImageSrc(itemData, baseUrl, merchantId = "") {
  if (!itemData) return "";

  let img =
    itemData.product_image ||
    itemData.image ||
    itemData.product_image_url ||
    itemData.img ||
    "";

  // API may return comma-separated img (e.g. "1771909542_e788c7.jpeg,1771909543_992478.jpeg")
  if (img && img.includes(",")) {
    img = img.split(",")[0]?.trim() || img;
  }

  if (!img) return "";

  // Already a full URL
  if (img.startsWith("http://") || img.startsWith("https://")) {
    return img;
  }

  const base = (baseUrl || "").replace(/\/$/, "");
  const path = img.replace(/^\//, "");

  // Path already includes upload/products/...
  if (path.startsWith("upload/products/")) {
    return base ? `${base}/${path}` : path;
  }

  // New order list API: img is filename only — e.g. "1781067820_b1021d.png"
  const storeId = String(merchantId || itemData.merchant_id || "").trim();
  if (storeId) {
    return `${base}/upload/products/${storeId}/${path}`;
  }

  return base ? `${base}/${path}` : path;
}

/**
 * Refund status codes (per item, after grouping):
 * 0 = No refund
 * 1 = Full refund (all qty refunded)
 * 2 = Partial refund (some qty refunded)
 */
export const REFUND_STATUS = {
  NO_REFUND: 0,
  FULL_REFUND: 1,
  PARTIAL_REFUND: 2,
};

/**
 * Order-level refund status for card UI.
 */
export const ORDER_REFUND_STATUS = {
  NO_REFUND: "NO_REFUND",
  FULL_REFUND: "FULL_REFUND",
  PARTIAL_REFUND: "PARTIAL_REFUND",
};

/**
 * Normalizes product name for grouping (trim, lowercase, collapse whitespace).
 * Used when product_id/variant_id/upc are missing (e.g. product deleted from DB).
 *
 * @param {Object} item - Raw cart item
 * @returns {string} Normalized name for grouping
 */
function getNormalizedProductNameForGrouping(item) {
  const raw =
    item?.product_name ?? item?.name ?? item?.item_name ?? "";
  return String(raw)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * Returns a grouping key for an item.
 * Priority: 1) product_id + variant_id (strict), 2) upc, 3) normalized product name.
 * Handles missing product_id/variant_id when product is deleted from DB.
 *
 * @param {Object} item - Raw cart item
 * @returns {string} Group key
 */
/**
 * Trimmed order_items.custom_note, or "" when missing/blank/non-text.
 * @param {object} item
 * @returns {string}
 */
export function getOrderItemCustomNote(item) {
  const note = item?.custom_note;
  if (typeof note !== "string" && typeof note !== "number") return "";
  const trimmed = String(note).trim();
  return trimmed.toLowerCase() === "null" ? "" : trimmed;
}

function getItemGroupKey(item) {
  const productId = item?.product_id;
  const variantId = item?.variant_id;
  const upc = item?.upc ?? item?.sku ?? "";

  const hasProductId =
    productId != null && String(productId).trim() !== "";
  const hasVariantId =
    variantId != null && String(variantId).trim() !== "";
  const hasUpc = upc != null && String(upc).trim() !== "";

  // 1. Strict grouping by product_id + variant_id when available
  if (hasProductId) {
    return `pid_${productId}_vid_${hasVariantId ? variantId : "null"}`;
  }

  // 2. Fallback: upc when product_id/variant_id missing
  if (hasUpc) {
    return `upc_${String(upc).trim()}`;
  }

  // 3. Last fallback: similar product name (normalized) when product_id, variant_id, upc all missing
  const normalizedName = getNormalizedProductNameForGrouping(item);
  return `name_${normalizedName || "unknown"}`;
}

/**
 * Groups cart items by product_id + variant_id (strict), or by upc/product name when missing.
 * Sums qty/refund_qty/refund_amount and calculates refund status per group.
 * Handles products deleted from DB (missing product_id/variant_id) via upc or product name fallback.
 *
 * @param {Array} items - Raw cart items from API (cart_data, order_product, etc.)
 * @returns {Array} Grouped items with is_refunded: 0|1|2
 */
export function getGroupedItemsWithRefundStatus(items) {
  if (!Array.isArray(items) || items.length === 0) return [];

  const grouped = Object.values(
    items.reduce((acc, item) => {
      const note = getOrderItemCustomNote(item);
      const key = note
        ? `${getItemGroupKey(item)}__note_${note}`
        : getItemGroupKey(item);

      if (!acc[key]) {
        acc[key] = {
          ...item,
          qty: 0,
          refund_qty: 0,
          refund_amount: 0,
        };
      }

      acc[key].qty += Number(item.qty) || 0;
      acc[key].refund_qty += Number(item.refund_qty) || 0;
      acc[key].refund_amount += Number(item.refund_amount) || 0;

      return acc;
    }, {})
  ).map((item) => {
    const totalQty = item.qty; // remaining/kept quantity
    const totalRefundQty = item.refund_qty;

    let finalRefundStatus = REFUND_STATUS.NO_REFUND;
    if (totalRefundQty === 0) {
      finalRefundStatus = REFUND_STATUS.NO_REFUND;
    } else if (totalQty === 0 && totalRefundQty > 0) {
      // All refunded (nothing left - no items kept)
      finalRefundStatus = REFUND_STATUS.FULL_REFUND;
    } else {
      // Some kept (totalQty > 0) and some refunded (totalRefundQty > 0)
      finalRefundStatus = REFUND_STATUS.PARTIAL_REFUND;
    }

    return {
      ...item,
      is_refunded: finalRefundStatus,
    };
  });

  return grouped;
}

/**
 * Derives card-level refund status from grouped items.
 * If any item is kept (totalRemainingQty > 0) → partial_refund
 * If all refunded (totalRemainingQty === 0) and any refund exists → full_refund
 * Else → no_refund
 *
 * @param {Array} groupedItems - Items from getGroupedItemsWithRefundStatus
 * @returns {"no_refund"|"full_refund"|"partial_refund"}
 */
export function getCardRefundStatusFromItems(groupedItems) {
  if (!Array.isArray(groupedItems) || groupedItems.length === 0) {
    return "no_refund";
  }
  const totalRemainingQty = groupedItems.reduce(
    (sum, i) => sum + (Number(i.qty) || 0),
    0
  );
  const hasAnyRefund = groupedItems.some(
    (i) =>
      i.is_refunded === REFUND_STATUS.FULL_REFUND ||
      i.is_refunded === REFUND_STATUS.PARTIAL_REFUND
  );

  // Any item kept + any refund = partial (e.g. product1 fully refunded, product2 has 1 kept)
  if (totalRemainingQty > 0 && hasAnyRefund) {
    return "partial_refund";
  }
  // All refunded, nothing kept
  if (totalRemainingQty === 0 && hasAnyRefund) {
    return "full_refund";
  }
  return "no_refund";
}

/**
 * Computes high-level refund status for an order using:
 * - m_status (7 = fully refunded)
 * - is_partial_refund flag
 * - sum of item qty vs sum of refund_qty
 *
 * This matches the UI rules:
 * - m_status === 7 → fully refunded
 * - If all qty is refunded (totalQty === totalRefundQty) → fully refunded
 * - If some qty refunded but not all → partial refund
 * - Otherwise → no refund
 */
export function getOrderRefundStatus(order) {
  if (!order) return ORDER_REFUND_STATUS.NO_REFUND;

  const od = order.order_detail || {};

  const mStatus = String(order.m_status ?? od.m_status ?? "").trim();
  const isPartialFlag =
    String(order.is_partial_refund ?? od.is_partial_refund ?? "0") === "1";

  const refundRaw =
    order.refund_amount != null && order.refund_amount !== ""
      ? order.refund_amount
      : od.refund_amount != null && od.refund_amount !== ""
        ? od.refund_amount
        : 0;
  const refundAmountNum = Number(refundRaw) || 0;

  const rawItems =
    order.items ||
    order.cart_data ||
    order.order_product ||
    order.order_products ||
    od.cart_data ||
    od.order_product ||
    od.order_products ||
    [];
  const items = Array.isArray(rawItems) ? rawItems : [];

  // Group items using the same logic we use for item-level refund status
  const groupedItems = getGroupedItemsWithRefundStatus(items);

  const hasAnyRefundInItems = groupedItems.some(
    (g) =>
      g.is_refunded === REFUND_STATUS.FULL_REFUND ||
      g.is_refunded === REFUND_STATUS.PARTIAL_REFUND
  );

  const hasRefund = refundAmountNum > 0 || hasAnyRefundInItems;

  // 1) m_status = 7 ⇒ always fully refunded
  if (mStatus === "7") {
    return ORDER_REFUND_STATUS.FULL_REFUND;
  }

  // 2) If we have grouped items: derive status from them
  if (groupedItems.length > 0 && hasAnyRefundInItems) {
    const allFullyRefunded = groupedItems.every(
      (g) => g.is_refunded === REFUND_STATUS.FULL_REFUND
    );

    if (allFullyRefunded) {
      return ORDER_REFUND_STATUS.FULL_REFUND;
    }

    // Mix of full / partial / no-refund groups → partial at order level
    return ORDER_REFUND_STATUS.PARTIAL_REFUND;
  }

  // 3) Fallback when no item details but we do have refund amount
  if (hasRefund && !groupedItems.length) {
    return ORDER_REFUND_STATUS.FULL_REFUND;
  }

  // 4) Fallback: API explicitly says partial and we see some refund
  if (isPartialFlag && hasRefund) {
    return ORDER_REFUND_STATUS.PARTIAL_REFUND;
  }

  // 5) No effective refund
  return ORDER_REFUND_STATUS.NO_REFUND;
}

/**
 * In-store refund status — mirrors Android OfflineOrderTabAdapter + PHP orders/order_list:
 * - m_status === 7 → fully refunded
 * - any other order with my_order.is_refunded = 1 → partial refund (even if every item was refunded)
 *
 * The new-UI list API does not return my_order.is_refunded, so on a non-7 order any refund
 * signal (is_partial_refund, refund_amount, item refund_qty / is_refunded) stands in for it.
 */
export function getInStoreOrderRefundStatus(order) {
  if (!order) return ORDER_REFUND_STATUS.NO_REFUND;

  const od = order.order_detail || {};
  const pick = (key) => order[key] ?? od[key];
  const isFlagOn = (value) => String(value ?? "").trim() === "1";

  if (String(pick("m_status") ?? "").trim() === "7") {
    return ORDER_REFUND_STATUS.FULL_REFUND;
  }

  if (
    isFlagOn(pick("is_refunded")) ||
    isFlagOn(pick("is_partial_refund")) ||
    (Number(pick("refund_amount")) || 0) > 0
  ) {
    return ORDER_REFUND_STATUS.PARTIAL_REFUND;
  }

  const rawItems =
    order.items ||
    order.cart_data ||
    order.order_product ||
    order.order_products ||
    od.cart_data ||
    od.order_product ||
    od.order_products ||
    [];
  const hasItemRefund =
    Array.isArray(rawItems) &&
    rawItems.some(
      (item) =>
        (Number(item?.refund_qty) || 0) > 0 ||
        ["1", "2"].includes(String(item?.is_refunded ?? "").trim())
    );

  return hasItemRefund ? ORDER_REFUND_STATUS.PARTIAL_REFUND : ORDER_REFUND_STATUS.NO_REFUND;
}

/** Resolve pickup vs delivery (API field or tab fallback: Pickup / Delivery / All). */
function resolveOnlineOrderMethod(order, orderMethodFallback = "") {
  const raw = String(order?.order_method || orderMethodFallback || "")
    .trim()
    .toLowerCase();
  if (raw === "delivery") return "delivery";
  if (raw === "pickup") return "pickup";
  return raw;
}

function resolveOnlineOrderPaymentId(order) {
  const paymentId = String(order?.payment_id ?? "").trim();
  if (paymentId && paymentId.toLowerCase() !== "null") return paymentId;

  const paymentMethod = String(
    order?.payment_method ?? order?.paymentMethod ?? ""
  ).trim();
  if (paymentMethod && paymentMethod.toLowerCase() !== "null") return paymentMethod;

  return "";
}

/**
 * Paid / active order branch from old orderStatus() — cash orders often have no
 * payment_result even when the listing shows them as paid.
 */
function hasOnlineOrderPaidPayment(order, isCashPayment = false) {
  const orderStatus = String(order?.order_status ?? "").toLowerCase();
  if (orderStatus === "open") return false;

  // Authoritative paid signal from the order_list API. Some paid orders omit
  // payment_result (only order_status: "paid" + payment_method is present), so
  // don't require payment_result === "Approved" when the order is already paid.
  if (orderStatus === "paid") return true;

  // Card already resolved cash via paymentType / paymentDisplayType
  if (isCashPayment) return true;

  const paymentId = resolveOnlineOrderPaymentId(order);
  if (!paymentId || paymentId === "null") return false;

  if (paymentId.toLowerCase() === "cash") return true;

  const paymentResult = String(order?.payment_result ?? "").trim();
  if (paymentResult.toLowerCase() === "approved") return true;

  return false;
}

/**
 * True when old Orders Online UI status dropdown shows "Cancel" (value "5")
 * and it is not disabled — same rules as orderStatus() in
 * src/Components/Orders/OnlineOrder/OnlineTableViewData.js (All + New tabs).
 *
 * Permission (MENU_CANCEL_ORDER) is enforced separately via canCancelOrder,
 * matching handleDropDownChangeSafe when option.value === "5".
 *
 * @param {boolean} [options.isCashPayment] — from card; helps paid check for cash without payment_result
 * @param {boolean} [options.isIncompleteOrder]
 * @param {boolean} [options.hasRefundStatus]
 */
export function canShowOnlineCancelOrderOption(
  order,
  {
    canCancelOrder = true,
    orderMethodFallback = "",
    isCashPayment = false,
    isIncompleteOrder = false,
    hasRefundStatus = false,
  } = {}
) {
  if (!order || !canCancelOrder) return false;

  if (isIncompleteOrder || hasRefundStatus) return false;

  if (!hasOnlineOrderPaidPayment(order, isCashPayment)) return false;

  const mStatus = String(order.m_status ?? "");
  const method = resolveOnlineOrderMethod(order, orderMethodFallback);
  const isPickup = method === "pickup";
  const isDelivery = method === "delivery";
  const hasDeliveryId = !!order.delivery_id;

  // orderStatus() early returns — no dropdown rendered
  if (mStatus === "5") return false;
  if (mStatus === "7") return false;
  if (mStatus === "4" && isPickup) return false;
  if (mStatus === "4" && isDelivery) return false;

  // dropdownOptions — Cancel (value "5") in status lists / appended for Uber
  let cancelInDropdown;
  if (hasDeliveryId) {
    cancelInDropdown = true;
  } else if (isDelivery && mStatus === "3") {
    cancelInDropdown = false;
  } else {
    cancelInDropdown = true;
  }

  if (!cancelInDropdown) return false;

  // Uber (delivery_id): old UI keeps the whole status dropdown read-only EXCEPT when the
  // order is "Ready" (delivery + m_status 6), where only the Cancel option is clickable.
  // For every other locked state (m_status 1/2/3 with delivery_id) Cancel is disabled.
  if (hasDeliveryId) {
    return isDelivery && mStatus === "6";
  }

  // Non-Uber delivery Out for Delivery — Cancel removed from old dropdown
  if (isDelivery && mStatus === "3") return false;

  return true;
}

/** Pickup Picked Up / Delivery Delivered — m_status 4; status must not change. */
export function isOnlineOrderPickedUpOrDelivered(order) {
  if (!order) return false;
  return String(order.m_status ?? "").trim() === "4";
}
